import { createContext, useContext } from 'react'
import { gameStatus, sentenceCase, versionLabel } from '@aurora/core'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { currentVersion, gameVersions } from '../../store/selectors'
import { madeSummary, openGame, playGame } from '../../lib/games'
import { GameFace, GameRow, PlayIcon } from '../playroom/GameRow'

/**
 * The versions of the game whose page this conversation is on. There the
 * page itself is the game: its banner is the one live status of a change,
 * and its hero is the version that plays, so a card for the same game in
 * the conversation says only which version it was.
 */
export const GamePageVersions = createContext<ReadonlySet<string>>(new Set())

const KIND_LABEL: Record<string, string> = {
  rubric: 'Rubric',
  plan: 'Plan',
  activity: 'Activity',
  deck: 'Cards',
  document: 'Document',
  book: 'Book',
  report: 'Report',
  photo: 'Photo',
}

/**
 * An artifact referenced from a message.
 *
 * `expanded` gives the richer card the agent's own output deserves; the compact
 * form is for things the parent attached.
 */
export function ArtifactChip({ artifactId, expanded, queued }: {
  artifactId: string
  expanded?: boolean
  /** The reply asked for a change that waits on this version being made: show that change, not this version. */
  queued?: { label?: string }
}) {
  const artifact = useAuroraStore((s: any) => s.artifacts[artifactId])
  const current = useAuroraStore((s: any) => (expanded && artifact?.kind === 'activity' ? currentVersion(s, artifactId) : undefined))
  const page = useContext(GamePageVersions)
  const onItsPage = page.has(artifactId)
  if (!artifact) return null
  if (queued && artifact.kind === 'activity') return <Waiting artifact={artifact} label={queued.label} />
  // A card posted with an older version stays in the conversation as history,
  // never as a Play that hands her the old game.
  if (onItsPage) return expanded ? <VersionNote artifact={artifact} playsNow={current?._id === artifactId} /> : null
  if (current && current._id !== artifactId && (current.version ?? 1) > (artifact.version ?? 1)) {
    return <Replaced artifact={artifact} current={current} />
  }

  const open = () => {
    const s = auroraStore.getState() as any
    s.selectArtifact(artifactId)
    if (artifact.kind === 'activity') s.openGame(artifactId)
    else if (artifact.kind === 'plan') s.setView('today')
    else if (artifact.kind === 'rubric') s.setView('story')
    else if (artifact.kind === 'deck') s.setView('playroom')
    else s.setView('library')
  }

  // A game the agent made or changed shows as the game itself: live while it is being made, playable once it is ready.
  // On another game's page it is named as another game, so it never reads as a version of this one.
  if (expanded && artifact.kind === 'activity') {
    return page.size ? (
      <div className="artifact-other">
        <span className="artifact-other-k">Also changed</span>
        <GameRow artifact={artifact} />
      </div>
    ) : <GameRow artifact={artifact} />
  }

  const photoUrl = artifact.kind === 'photo' ? artifact.storage?.url : undefined
  if (!expanded) {
    return photoUrl ? (
      <button className="artifact-photo" onClick={open} aria-label={artifact.title}>
        <img src={photoUrl} alt={artifact.title} loading="lazy" />
      </button>
    ) : (
      <button className="artifact-chip" onClick={open}>
        {artifact.title}
      </button>
    )
  }

  // A deck's line is read from the deck as it is now, so an edit (fewer a
  // day, resting) shows here too, not the words written when it was made.
  const summary = madeSummary(artifact)

  return (
    <button className="artifact-card" onClick={open}>
      <span className="artifact-card-kind">{KIND_LABEL[artifact.kind] ?? artifact.kind}</span>
      <span className="artifact-card-title">{artifact.title}</span>
      {summary && <span className="artifact-card-summary">{summary}</span>}
      {artifact.version > 1 && (
        <span className="artifact-card-version">v{artifact.version}</span>
      )}
    </button>
  )
}

/**
 * A version a newer one replaced, anywhere but the game's own page: its
 * number and a quiet way to the version that plays now.
 */
function Replaced({ artifact, current }: { artifact: any; current: any }) {
  const now = current.version ?? 'the newest'
  return (
    <p className="artifact-replaced">
      <span>Version {artifact.version ?? 1} · replaced by version {now}</span>
      <button className="btn btn-quiet btn-sm" onClick={() => openGame(current._id)}>
        Open version {now}
      </button>
    </p>
  )
}

/**
 * On the game's own page, Sapling's card for one of its versions: the
 * game's face, which version it made and what changed. The one that plays
 * now is the hero's to hand over, so here it only says so, the way the
 * Versions rail does; an earlier version that is ready can still be played
 * from its card. Where a version being made stands is the banner's to say.
 */
function VersionNote({ artifact, playsNow }: { artifact: any; playsNow: boolean }) {
  const label = versionLabel(artifact)
  const ready = gameStatus(artifact) === 'ready'
  return (
    <p className="artifact-version">
      <span className="game-thumb" aria-hidden="true">
        {ready ? <GameFace artifact={artifact} /> : <span className="game-thumb-making is-still" />}
      </span>
      <span className="artifact-version-text">
        <span className="artifact-version-n">Version {artifact.version ?? 1}</span>
        {label && <span className="artifact-version-label">{label}</span>}
      </span>
      {ready && (playsNow ? (
        <span className="artifact-version-now">Playing now</span>
      ) : (
        <button className="btn btn-quiet btn-sm artifact-version-play" onClick={() => playGame(artifact._id)}>
          <PlayIcon /> Play
        </button>
      ))}
    </p>
  )
}

/**
 * A change asked for while another version was being made. It waits, and
 * becomes the next version by itself when that one is ready; from then on
 * its card is that version's own.
 */
function Waiting({ artifact, label }: { artifact: any; label?: string }) {
  const after = (artifact.version ?? 1) as number
  const next = useAuroraStore((s: any) =>
    gameStatus(artifact) === 'making' ? undefined : gameVersions(s, artifact._id).find((v: any) => (v.version ?? 1) > after),
  ) as any
  if (next) return <ArtifactChip artifactId={next._id} expanded />
  return (
    <p className="artifact-version is-waiting">
      <span className="game-thumb" aria-hidden="true"><span className="game-thumb-making is-still" /></span>
      <span>
        <span className="artifact-version-n">Waiting · becomes version {after + 1}</span>
        {label?.trim() && <span className="artifact-version-label">{sentenceCase(label.trim())}</span>}
        <span className="artifact-version-when">Starts when version {after} is ready</span>
      </span>
    </p>
  )
}

